import React, { useEffect, useState } from "react";
import "./TableroFinanzas.css";
import { getTablero } from "../../../services/finanzas";
import { toast } from "react-toastify";

/* ---------------- utilidades ---------------- */

const toNum = (v) => {
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
};


const MODOS = [
  { id: "hoy", label: "Hoy" },
  { id: "semana", label: "Semana" },
  { id: "mes", label: "Mes" },
  { id: "anio", label: "Año" },
  { id: "personalizado", label: "Personalizado" },
];

const VERDICTO = {
  sube: { color: "verde", texto: "Vas mejor" },
  baja: { color: "ambar", texto: "A la baja" },
  igual: { color: "neutro", texto: "Igual que antes" },
};

/* ---------------- piezas ---------------- */

function MiniBarras({ serie }) {
  if (!serie || !serie.length) return null;
  const n = serie.length;
  const gap = 8;
  const bw = (320 - gap * (n - 1)) / n;
  const max = Math.max(1, ...serie.map((s) => toNum(s.valor)));
  return (
    <svg className="tf-barras" viewBox="0 0 320 64" role="img" aria-label="Tendencia">
      {serie.map((s, i) => {
        const h = Math.max(6, Math.round((toNum(s.valor) / max) * 48));
        return (
          <rect
            key={i}
            x={i * (bw + gap)}
            y={56 - h}
            width={bw}
            height={h}
            rx="3"
            className={s.en_curso ? "tf-bar tf-bar--encurso" : "tf-bar"}
          />
        );
      })}
    </svg>
  );
}

function Barra({ progreso }) {
  return (
    <div className="tf-progreso">
      <div className="tf-progreso-fill" style={{ width: `${Math.round((progreso || 0) * 100)}%` }} />
    </div>
  );
}

// Tarjeta genérica: pregunta + valor + semáforo + cuerpo desplegable (con la "i").
function Tarjeta({ id, pregunta, valor, semaforo, resumen, info, abierta, onToggle, children }) {
  return (
    <div className={`tf-card ${abierta ? "is-open" : ""}`}>
      <button className="tf-card-head" onClick={() => onToggle(id)} aria-expanded={abierta}>
        <span className="tf-card-info">
          <span className="tf-card-pregunta">{pregunta}</span>
          <span className="tf-card-valor">{valor}</span>
          {resumen && (
            <span className={`tf-verdict tf-verdict--${semaforo}`}>
              <span className="tf-dot" /> {resumen}
            </span>
          )}
        </span>
        <span className="tf-chevron" aria-hidden="true">⌄</span>
      </button>
      <div className="tf-card-body">
        <div className="tf-card-body-inner">
          {children}
          {info && (
            <p className="tf-comocalcula">
              <span className="tf-i">i</span> {info}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- componente principal ---------------- */

export default function TableroFinanzas() {
  const [modo, setModo] = useState("hoy");
  const [comparar, setComparar] = useState("periodo_anterior");
  const [iniCustom, setIniCustom] = useState("");
  const [finCustom, setFinCustom] = useState("");
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [abierta, setAbierta] = useState(null);
  const [aplicar, setAplicar] = useState(0); // se incrementa al picar "Aplicar"
   // Fecha local en YYYY-MM-DD (NO usar toISOString: convierte a UTC y atrasa el día)
  const hoyISO = (() => {
    const d = new Date();
    const off = d.getTimezoneOffset() * 60000; // minutos -> ms
    return new Date(d.getTime() - off).toISOString().slice(0, 10);
  })();

  const cargar = async () => {
    if (modo === "personalizado" && (!iniCustom || !finCustom)) return; // sin fechas aún, no llama
    setCargando(true);
    setError(false);
    const res = await getTablero(modo, comparar, iniCustom, finCustom);
    if (res) setData(res);
    else setError(true);
    setCargando(false);
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, comparar, aplicar]);

  const toggle = (id) => setAbierta((p) => (p === id ? null : id));

  // ----- comparación de una métrica de flujo -----
  const flujo = (card) => {
    if (!card) return { color: "neutro", resumen: "" };
    if (card.comparable === false)
      return { color: "neutro", resumen: "Aún falta historial para comparar." };
    if (card.muy_pronto) return { color: "neutro", resumen: "Muy pronto para comparar" };
    const v = VERDICTO[card.direccion] || VERDICTO.igual;
    let delta = "";
    if (card.delta_pct !== null && card.delta_pct !== undefined) {
      delta = `${card.delta_pct > 0 ? "+" : ""}${card.delta_pct}%`;
    } else if (card.delta_abs !== null && card.delta_abs !== undefined) {
      const n = toNum(card.delta_abs);
      delta = `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n)}`;
    }
    return { color: v.color, resumen: `${v.texto}${delta ? ` · ${delta}` : ""}` };
  };

  // ----- métrica de por vida con estado de madurez -----
  const madurez = (m) => {
    if (!m) return { activa: true, locked: null };
    if (m.estado === "sin_datos")
      return { activa: false,  locked: "Este indicador se desbloquea con más pacientes atendidos en consulta. Sigue utilizando Salvir.", progreso: null };
    if (m.estado === "madurando")
      return {
        activa: false,
       locked: `Este indicador se desbloquea con ${m.faltan} ${m.faltan === 1 ? "paciente atendido en consulta más" : "pacientes atendidos en consulta más"}`,
        progreso: m.progreso,
      };
    return { activa: true, locked: null };
  };

  const resumenRetencion = (card, sufijo = "") => {
  if (!card) return { color: "neutro", resumen: "" };

  if (card.comparable === false)
    return { color: "neutro", resumen: "Aún falta historial para comparar." };

  if (card.muy_pronto)
    return { color: "neutro", resumen: "Muy pronto para comparar" };

  if (card.comparativo_suficiente === false)
    return { color: "neutro", resumen: "Sin comparativo suficiente" };

  const v = VERDICTO[card.direccion] || VERDICTO.igual;

  let delta = "";
  if (card.delta_abs !== null && card.delta_abs !== undefined) {
    const n = toNum(card.delta_abs);
    delta = `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n)}${sufijo}`;
  }

  return {
    color: v.color,
    resumen: `${v.texto}${delta ? ` · ${delta}` : ""}`,
  };
};

  if (cargando) return <div className="tf-wrap tf-estado">Cargando tu consultorio…</div>;
  if (error || !data)
    return (
      <div className="tf-wrap tf-estado">
        <p>No pudimos cargar tu tablero.</p>
        <button className="tf-retry" onClick={cargar}>Volver a intentar</button>
      </div>
    );

  const s = data.secciones;
  const disp = data.comparaciones_disponibles || {};
  const registro = data.registro || undefined; // fecha de alta (YYYY-MM-DD) para el tope hacia atrás
  const comp = data.comparativo || {};

  // "lunes 16 de junio" a partir de "YYYY-MM-DD" (sin que UTC recorra el día)
  const fechaLarga = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("es-MX", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  };

  const rangoTexto =
    data.rango_actual.inicio === data.rango_actual.fin
      ? fechaLarga(data.rango_actual.inicio)
     : `Del ${fechaLarga(data.rango_actual.inicio)} al ${fechaLarga(data.rango_actual.fin)}`;

  const compTexto = comp.comparable
    ? comp.inicio === comp.fin
      ? `comparando contra ${fechaLarga(comp.inicio)}`
      : `comparando contra del ${fechaLarga(comp.inicio)} al ${fechaLarga(comp.fin)}`
    :"Aún falta historial para comparar tus indicadores. Pronto podrás ver tu avance.";
  
  return (
    <div className="tf-wrap">
      {/* selector de rango */}
      <h2>Indicadores</h2>
      <h3>Resumen de tu práctica</h3>
      <div className="tf-toggle">
        {MODOS.map((m) => (
          <button
            key={m.id}
            className={modo === m.id ? "is-active" : ""}
            onClick={() => setModo(m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>

      {modo === "personalizado" && (
        <div className="tf-fechas">
         <label>Del <input type="date" min={registro} max={finCustom || hoyISO} value={iniCustom} onChange={(e) => setIniCustom(e.target.value)} /></label>
          <label>al <input type="date" min={iniCustom || registro} max={hoyISO} value={finCustom} onChange={(e) => setFinCustom(e.target.value)} /></label>
          <button
            className="tf-aplicar"
            disabled={!iniCustom || !finCustom}
            onClick={() => {
               const limpiar = (msg) => {
                toast.error(msg);
                setIniCustom("");
                setFinCustom("");
              };
              if (registro && iniCustom < registro) {
               return limpiar("No puedes elegir fechas anteriores a tu registro.");
              }
              if (finCustom > hoyISO) {
               return limpiar("La fecha final no puede ser posterior a hoy.");
              }
              if (iniCustom > finCustom) {
                 return limpiar("La fecha inicial no puede ser posterior a la final.");
              }
              setAplicar((n) => n + 1);
            }}
          >
            Aplicar
          </button>
        </div>
      )}

      {/* selector de comparación (deshabilita lo que no aplica) */}
      <div className="tf-comparar">
        <span className="tf-comparar-lbl">Comparar contra:</span>
        <button
          className={comparar === "periodo_anterior" ? "is-active" : ""}
          disabled={disp.periodo_anterior === false}
          onClick={() => setComparar("periodo_anterior")}
        >
          Periodo anterior
        </button>
        <button
          className={comparar === "anio_pasado" ? "is-active" : ""}
          disabled={disp.anio_pasado === false}
          onClick={() => setComparar("anio_pasado")}
          title={disp.anio_pasado === false ? "Se desbloquea con más historia en Salvir" : ""}
        >
          Año pasado
        </button>
      </div>
       <p className="tf-contexto">
        <strong>{rangoTexto}</strong> · {compTexto}
      </p>

      {/* ===== Sección 1: ¿Quién llega? ===== */}
      <h3 className="tf-seccion">Registro y atención de pacientes</h3>
      <div className="tf-grid">
       {(() => {
          const c = s.quien_llega.pacientes_registrados;
          const total = c.valor || 0;
          const f = flujo(c);

          return (
            <Tarjeta
              id="registrados"
              pregunta="Pacientes registrados"
              valor={total}
              semaforo={total === 0 ? "neutro" : f.color}
              resumen={total === 0 ? "No se registraron pacientes en este periodo" : f.resumen}
              abierta={abierta === "registrados"}
              onToggle={toggle}
              info="Pacientes totales dados de alta dentro del periodo."
            >
              <MiniBarras serie={c.tendencia} />
            </Tarjeta>
          );
        })()}

       {(() => {
          const c = s.quien_llega.pacientes_nuevos;
          const total = c.valor || 0;
          const f = flujo(c);

          return (
            <Tarjeta
              id="nuevos"
              pregunta="Pacientes nuevos"
              valor={total}
              semaforo={total === 0 ? "neutro" : f.color}
              resumen={total === 0 ? "No hay pacientes nuevos en este periodo" : f.resumen}
              abierta={abierta === "nuevos"}
              onToggle={toggle}
              info="Pacientes cuya primera consulta fue en este periodo."
            >
              <MiniBarras serie={c.tendencia} />
            </Tarjeta>
          );
        })()}

      {(() => {
        const c = s.quien_llega.pacientes_recurrentes;
         const total = c.valor || 0;
        const f = flujo(c);

        return (
          <Tarjeta
            id="recurrentes"
            pregunta="Pacientes recurrentes"
            valor={total}
            semaforo={total === 0 ? "neutro" : f.color}
            resumen={total === 0 ? "No hay pacientes recurrentes en este periodo" : f.resumen}
            abierta={abierta === "recurrentes"}
            onToggle={toggle}
            info="Pacientes que tuvieron una consulta de seguimiento en este periodo. También pudieron haber sido nuevos en el mismo periodo."
          >
            <MiniBarras serie={c.tendencia} />
          </Tarjeta>
        );
      })()}

       
                {(() => {
          const c = s.cuanto_generas.kpi_conversion;
          if (c.sin_datos)
            return (
              <Tarjeta id="conv" pregunta="Registrados con consulta"
                valor="No aplica" semaforo="neutro"
                abierta={abierta === "conv"} onToggle={toggle}
                info="Aún no hay pacientes registrados en este periodo." />
            );
          const col = c.de_cada_10 >= 7 ? "verde" : c.de_cada_10 >= 4 ? "ambar" : "rojo";
          return (
            <Tarjeta id="conv" pregunta="Registrados con consulta"
              valor={`${c.porcentaje}%`} semaforo={col}
              resumen={`${c.con_consulta} de ${c.registrados} registrados`}
              abierta={abierta === "conv"} onToggle={toggle}
              info="Porcentaje de pacientes registrados en este periodo que ya tuvieron una consulta." />
          );
        })()}
      </div>

      {/* ===== Sección 2: agenda / actividad ===== */}
      <h3 className="tf-seccion">Actividad de consulta y agenda</h3>
      <div className="tf-grid">
        {(() => {
          const c = s.cuanto_generas.consultas;
          const f = flujo(c);
          return (
            <Tarjeta id="consultas" pregunta="Consultas"
              valor={c.valor} semaforo={c.valor === 0 ? "neutro" : f.color}
              resumen={c.valor === 0 ? "No hubo consultas en este periodo" : f.resumen}
              abierta={abierta === "consultas"} onToggle={toggle}
              info="Total consultas dentro del periodo." />
          );
        })()}
        {(() => {
          const c = s.cuanto_generas.citas_agendadas;
          if (c.no_disponible)
            return (
              <Tarjeta id="citas" pregunta="Citas con pacientes"
                valor="No disponible" semaforo="neutro"
                abierta={abierta === "citas"} onToggle={toggle}
               info="No se pudo calcular esta métrica con la información disponible." />
            );
         const f = flujo(c);
          const total = c.valor ?? c.total ?? 0;

          return (
            <Tarjeta id="citas" pregunta="Citas con pacientes"
              valor={total}
              semaforo={total === 0 ? "neutro" : f.color}
              resumen={total === 0 ? "No hubo citas con pacientes en este periodo" : f.resumen}
              abierta={abierta === "citas"} onToggle={toggle}
              info="Citas del periodo ligadas a un paciente. Se cuentan por la fecha de la cita, no por la fecha en que fueron agendadas. Incluye canceladas, no incluye bloqueos personales." />
          );
        })()}

        {(() => {
          const c = s.cuanto_generas.ocupacion;
          if (c.sin_horario || c.no_disponible)
            return (
              <Tarjeta id="ocupacion" pregunta="Ocupación de tu agenda"
                valor="Configura tu horario" semaforo="neutro"
                abierta={abierta === "ocupacion"} onToggle={toggle}
                info="Necesitas tu horario semanal configurado para calcular la ocupación." />
            );
          const col = c.porcentaje > 100 ? "rojo" : c.porcentaje >= 60 ? "verde" : c.porcentaje >= 30 ? "ambar" : "neutro";
          return (
            <Tarjeta id="ocupacion" pregunta="Ocupación de tu agenda"
              valor={`${c.porcentaje}%`} semaforo={col}
              resumen={`${c.horas_citas} de ${c.horas_disponibles} hrs`}
              abierta={abierta === "ocupacion"} onToggle={toggle}
              info="Horas ocupadas por citas con pacientes entre las horas de tu horario. No incluye bloqueos personales ni cancelaciones. Arriba de 100% significa que agendaste más de lo que cubre tu horario.">
              {c.porcentaje > 100 && (
                <p className="tf-tip">Estás sobre tu capacidad: agendaste más horas de las que tu horario cubre.</p>
              )}
            </Tarjeta>
          );
        })()}

        {(() => {
          const c = s.cuanto_generas.citas_por_dia;
          if (c.no_disponible)
            return (
              <Tarjeta id="citasdia" pregunta="Citas por día"
                valor="No disponible" semaforo="neutro"
                abierta={abierta === "citasdia"} onToggle={toggle}
                info="No se pudo calcular esta métrica con la información disponible." />
            );
          return (
            <Tarjeta id="citasdia" pregunta="Citas por día"
             valor={`${c.promedio}`}
              semaforo={c.promedio === 0 ? "rojo" : c.promedio < 1 ? "ambar" : "verde"}
              resumen={`${c.total} en ${c.dias} días trabajados`}
              abierta={abierta === "citasdia"} onToggle={toggle}
              info="Promedio diario de citas con pacientes en días con horario configurado. No incluye bloqueos personales ni cancelaciones."/>
          );
        })()}
       

       {(() => {
          const r = s.cuanto_generas.kpi_recetas;

          if (r === null || r === undefined)
            return (
              <Tarjeta id="recetas" pregunta="Recetas"
                valor="No disponible" semaforo="neutro"
                abierta={abierta === "recetas"} onToggle={toggle}
                info="No se pudo calcular esta métrica con la información disponible." />
            );

          const f = flujo(r);
          const total = r.valor ?? 0;

          return (
            <Tarjeta id="recetas" pregunta="Recetas"
              valor={total}
              semaforo={total === 0 ? "neutro" : f.color}
              resumen={total === 0 ? "No hubo recetas en este periodo" : f.resumen}
              abierta={abierta === "recetas"} onToggle={toggle}
              info="Total recetas guardadas en el periodo." />
          );
        })()}
         {(() => {
          const c = s.quien_llega.citas_cancelacion;
          if (c.no_disponible)
            return (
              <Tarjeta id="cancel" pregunta="Citas: % de cancelación"
                valor="No disponible" semaforo="neutro"
                abierta={abierta === "cancel"} onToggle={toggle}
                info="Pendiente confirmar el modelo de citas para activar este número." />
            );
          if (c.sin_datos)
            return (
              <Tarjeta id="cancel" pregunta="Citas: % de cancelación"
                valor="No aplica" semaforo="neutro"
                abierta={abierta === "cancel"} onToggle={toggle}
                info="Aún no hay citas con pacientes en este periodo." />
            );
          const col = c.porcentaje <= 10 ? "verde" : c.porcentaje <= 25 ? "ambar" : "rojo";
          return (
            <Tarjeta id="cancel" pregunta="Porcentaje de cancelación de citas"
              valor={`${c.porcentaje}%`} semaforo={col}
              resumen={`${c.canceladas} de ${c.total} citas`}
              abierta={abierta === "cancel"} onToggle={toggle}
              info="De tus citas con pacientes, cuántas se cancelaron en el periodo. No incluye bloqueos personales."/>
          );
        })()}

      </div>

      {/* ===== Sección 3: ¿Quién se queda? ===== */}
      <h3 className="tf-seccion">Seguimiento y retención de pacientes</h3>
      <div className="tf-grid">
       {(() => {
          const c = s.quien_se_queda.regresan;
          const mh = madurez(c.madurez);
          const mp = madurez(c.madurez_periodo);
          const r = resumenRetencion(c, " pp");

          if (!mh.activa)
            return (
              <Tarjeta id="regresan" pregunta="¿Regresan tus pacientes?"
                valor={mh.locked}
                semaforo="neutro"
                abierta={abierta === "regresan"} onToggle={toggle}
                info="De tus pacientes atendidos, cuántos tuvieron al menos una consulta de seguimiento.">
                {mh.progreso !== null && <Barra progreso={mh.progreso} />}
              </Tarjeta>
            );

          if (!mp.activa)
            return (
              <Tarjeta id="regresan" pregunta="¿Regresan tus pacientes?"
                valor="Poca actividad en este periodo"
                semaforo="neutro"
                abierta={abierta === "regresan"} onToggle={toggle}
                info="De tus pacientes atendidos, cuántos tuvieron al menos una consulta de seguimiento.">
                {mp.progreso !== null && <Barra progreso={mp.progreso} />}
              </Tarjeta>
            );

          return (
            <Tarjeta id="regresan" pregunta="¿Regresan tus pacientes?"
              valor={`${c.de_cada_10} de cada 10`}
              semaforo={r.color}
              resumen={r.resumen}
              abierta={abierta === "regresan"} onToggle={toggle}
              info="De tus pacientes atendidos, cuántos tuvieron al menos una consulta de seguimiento.">
              <p className="tf-expl">De {c.pacientes_atendidos} pacientes atendidos, {c.regresaron} regresaron.</p>
              <p className="tf-tip">Antes de que el paciente se vaya, agéndale la siguiente cita.</p>
            </Tarjeta>
          );
        })()}

       {(() => {
          const c = s.quien_se_queda.recurrencia;
          const mh = madurez(c.madurez);
          const mp = madurez(c.madurez_periodo);
          const r = resumenRetencion(c, "");

          if (!mh.activa)
            return (
              <Tarjeta id="recurrencia" pregunta="Consultas por paciente"
                valor={mh.locked}
                semaforo="neutro"
                abierta={abierta === "recurrencia"} onToggle={toggle}
                info="Promedio de consultas por paciente atendido. Incluye primera consulta y seguimientos.">
                {mh.progreso !== null && <Barra progreso={mh.progreso} />}
              </Tarjeta>
            );

          if (!mp.activa)
            return (
              <Tarjeta id="recurrencia" pregunta="Consultas por paciente"
                valor="Poca actividad en este periodo"
                semaforo="neutro"
                abierta={abierta === "recurrencia"} onToggle={toggle}
                info="Promedio de consultas por paciente atendido. Incluye primera consulta y seguimientos.">
                {mp.progreso !== null && <Barra progreso={mp.progreso} />}
              </Tarjeta>
            );

          return (
            <Tarjeta id="recurrencia" pregunta="Consultas por paciente"
              valor={`${c.promedio} consultas`}
              semaforo={r.color}
              resumen={r.resumen}
              abierta={abierta === "recurrencia"} onToggle={toggle}
              info="Promedio de consultas por paciente atendido. Incluye primera consulta y seguimientos.">
              <p className="tf-expl">De {c.pacientes_atendidos} pacientes atendidos, hubo {c.consultas} consultas.</p>
            </Tarjeta>
          );
        })()}
      </div>
      <p className="tf-disclaimer">
        Los indicadores reflejan la actividad registrada en Salvir en un momento específico. Algunos indicadores pueden no sumar entre sí porque miden etapas distintas del flujo del paciente.
         <br />
  Algunas métricas necesitan más información para mostrarse correctamente.
         <br />
        Si modificas tu horario de atención, las métricas que dependen de agenda se recalculan con el horario vigente.
      </p>
    </div>
  );
}